import {
  CtaBuilderFn,
  InstantAnswerBuilderFn,
  InstantAnswerContext,
  InstantAnswerScenario,
} from "@faire/help-center-self-serve/types";
import { StrictLocalizeFunction } from "@faire/web--source/common/localization";
import { Button } from "@faire/web--source/slate/Button";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import articlePage from "@faire/web-api--source/routes/support/articles/articleId";
import React, { type JSX } from "react";

import {
  MessageRetailerOrBrandButton,
  additionalDataRequiredForContactingRetailerOrBrandCTA,
} from "../../shared/instantAnswerUtils";

import { ExtendReturnDateCTA } from "./ctas";
import { NEED_MORE_INFO_ANSWER } from "./shared";

export const ExtendReturnIA: InstantAnswerBuilderFn = (strictLocalize) => {
  return {
    titleBuilder: (context) => {
      return getAnswer(context, strictLocalize).getTitle();
    },

    descriptionBuilder: (context) => {
      return getAnswer(context, strictLocalize).getDescription();
    },

    primaryCtaBuilder: {
      textBuilder: (context) => {
        return getPrimaryCTA(context, strictLocalize)?.label;
      },
      linkBuilder: (context) => {
        return getPrimaryCTA(context, strictLocalize)?.getLink?.(
          context.brandOrder
        );
      },
    },

    secondaryCtaBuilder: {
      textBuilder: (context) => {
        return MessageRetailerOrBrandButton(context, strictLocalize)?.label;
      },
      onClickFunctionBuilder: (context) => {
        return MessageRetailerOrBrandButton(context, strictLocalize)?.onClick;
      },
    },
    additionalRequiredData:
      additionalDataRequiredForContactingRetailerOrBrandCTA(),
  };
};

const getAnswer = (
  context: InstantAnswerContext,
  strictLocalize: StrictLocalizeFunction
): InstantAnswerScenario => {
  const { brandOrder, shippingContext } = context;
  if (!brandOrder || !shippingContext) {
    return NEED_MORE_INFO_ANSWER(strictLocalize);
  }

  return shippingContext.is_return_extension_eligible
    ? RETURN_EXTENSION_ELIGIBLE(strictLocalize)
    : RETURN_EXTENSION_INELIGIBLE(strictLocalize);
};

const getPrimaryCTA: CtaBuilderFn = (context, strictLocalize) => {
  const { brandOrder, shippingContext } = context;
  if (
    !brandOrder ||
    !shippingContext ||
    !shippingContext.is_return_extension_eligible
  ) {
    return undefined;
  }

  return ExtendReturnDateCTA(strictLocalize);
};

const RETURN_EXTENSION_ELIGIBLE = (
  strictLocalize: StrictLocalizeFunction
): InstantAnswerScenario => ({
  getTitle: () => EXTEND_RETURN_WINDOW(strictLocalize),
  getDescription: () => [
    EXTEND_RETURN_WINDOW_FOR_NET_ORDER_TERMS(strictLocalize),
    OPTION_NOT_AVAILABLE_AFTER_RETURN_WINDOW(strictLocalize),
  ],
});

const RETURN_EXTENSION_INELIGIBLE = (
  strictLocalize: StrictLocalizeFunction
): InstantAnswerScenario => ({
  getTitle: () => NOT_ELIGIBLE_FOR_RETURN_EXTENSION(strictLocalize),
  getDescription: () => (
    <Typography>
      <StrictLocalMsg
        defaultMessage="Only orders purchased through our net 60 terms are eligible for return extensions. Extensions must be requested during the return window and can be requested once an order has been delivered. Read more about our <ReturnExtensionPolicyLinkFormatting>return extension policy</ReturnExtensionPolicyLinkFormatting> or message the brand to discuss alternative options."
        values={{
          ReturnExtensionPolicyLinkFormatting: LinkFormatting(
            articlePage("360022876131")
          ),
        }}
      />
    </Typography>
  ),
});

const EXTEND_RETURN_WINDOW = (strictLocalize: StrictLocalizeFunction) =>
  strictLocalize({
    defaultMessage: "Extend your return window",
    description: {
      text: "Title for the answer of extend return window issue if the selected order is return extension eligible",
    },
  });

const EXTEND_RETURN_WINDOW_FOR_NET_ORDER_TERMS = (
  strictLocalize: StrictLocalizeFunction
) =>
  strictLocalize({
    defaultMessage:
      "You have the ability to extend your return window for orders placed using net terms. For an additional 3% fee on orders that are eligible for returns, you can extend your return window to an additional 30 days.",
  });

const OPTION_NOT_AVAILABLE_AFTER_RETURN_WINDOW = (
  strictLocalize: StrictLocalizeFunction
) =>
  strictLocalize({
    defaultMessage:
      "Please note that the option to extend will no longer be available after your return window is closed. Once you request a return, it must be shipped by the due date on your return packing slip.",
  });

const NOT_ELIGIBLE_FOR_RETURN_EXTENSION = (
  strictLocalize: StrictLocalizeFunction
) =>
  strictLocalize({
    defaultMessage: "This order isn’t eligible for a return extension",
    description: {
      text: "Title for the answer of extend return window issue if the selected order is not return extension eligible",
    },
  });

const LinkFormatting = (linkTo: string) =>
  function (chunks: JSX.Element) {
    return (
      <Button linkTo={linkTo} variant="tertiary">
        {chunks}
      </Button>
    );
  };
